import { useTypographyStore } from "@redux/slices/typographySlice";
import { GlobalEnum } from "@utils/constants";
import { useTheme } from "styled-components/native";
import { formatItemValue } from "./types";
import React, { useMemo, useState } from "react";
import { LayoutChangeEvent, View } from "react-native";
import { Image as ExpoImage } from "expo-image";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import _ from "lodash";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { renderRequestStatus } from "@scenes/requests/utils/RequestsScreen";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { useTranslation } from "react-i18next";
import { VDBText } from "@components/atoms";
import { useColumnWidths } from "@components/molecules/SearchResultListItem/SearchResultContext";
import EmptyImagePreview from "@components/molecules/GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "@components/molecules/GalleryView/utils/EmptyImage/utils";
import TooltipText from "@components/molecules/SearchResultListItem/TooltipText";
import { getOptimizedListImageUrl } from "@components/molecules/Utils";
import {
  isLinkedPair,
  isPair,
  isParcel,
} from "@utils/product/productValidators";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { useSelector } from "react-redux";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { showRequestItemFromSupplier } from "../utils/utils";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import {
  useConsolidatedStoneTypes,
  useEnableRequestItemFromSupplier,
} from "@redux/slices/globalSlice";
import { handleDeepLink } from "@utils/product/productFormatters";
import VDBIcon from "@components/icons/VDBIcon";
import { consolidatedStoneTypesIcons } from "@components/molecules/SearchResultGridItem/utils";

const TabletListItem = ({
  item,
  isCustomApp,
  mItem,
  userCurrency,
  userInfo,
  _index,
  displayImage,
  fromBulkList,
  stockItemRequest,
  fromRequestDetail,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  handleLinkClick = false,
  requestItemFromSupplier,
}: {
  item?: any;
  isCustomApp?: boolean;
  mItem?: any;
  userCurrency?: any;
  userInfo?: any;
  _index?: any;
  displayImage?: boolean;
  fromBulkList?: boolean;
  fromRequestDetail?: boolean;
  stockItemRequest?: StockItemRequestClass;
  sectionName?: string;
  handleLinkClick?: any;
  requestItemFromSupplier?: boolean;
}) => {
  const theme = useTheme();
  const [imageUri, setImageUri] = useState(getOptimizedListImageUrl(mItem));
  const ImageComponent = ExpoImage;
  const enableRequestItemFromSupplier = isCustomApp
    ? useEnableRequestItemFromSupplier()
    : true;
  const { setWidth, columnWidths } = useColumnWidths();
  const columnWidth = useSelector((state) => state?.userInfo?.columnWidths);
  const { t } = useTranslation();
  const measureColumnWidth = (key: string, event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    const defaultWidth = item?.deafult_width?.split("px")[0];
    setWidth(key, width, Number(defaultWidth));
  };
  const defaultWidth =
    parseInt(item?.deafult_width?.split("px")[0], 10) +
    (theme.isTablet ? 0 : 1);
  const typography = useTypographyStore();
  const requestStatus = renderRequestStatus(stockItemRequest?.status || "", t);

  const productType: string = mItem?.type;
  const priceMode: number = userInfo.user.price_mode ?? "";
  const storageKey = `${productType}_${sectionName}`;
  const consolidatedStoneTypes = useConsolidatedStoneTypes();
  const consolidatedStoneTypesIconsData = consolidatedStoneTypesIcons(
    consolidatedStoneTypes
  );

  const getAvailabilityStatus = () => {
    switch (mItem?.available) {
      case GlobalEnum.OUT_ON_MEMO:
        return "error-01";
      case GlobalEnum.ON_HAND:
        return "success-01";
      case GlobalEnum.PLEASE_EMAIL:
      default:
        return "warn-01";
    }
  };

  const stoneTypeLowercase = useMemo(
    () =>
      isPair(mItem)
        ? SearchFilterTypeEnumLowercase.PAIRS
        : isParcel(mItem)
        ? SearchFilterTypeEnumLowercase.PARCEL
        : SearchFilterTypeEnumLowercase.SINGLE_STONES,
    [mItem]
  );

  if (!(item?.is_visible === undefined ? true : item.is_visible)) return null;
  const propertyText =
    sectionName === SearchFilterTypeEnum.PAIRS &&
    isLinkedPair(mItem) &&
    !item?.additional_attributes?.from_master
      ? mItem?.children?.map((child: any) =>
          formatItemValue(
            isCustomApp,
            item.response_key,
            child,
            userCurrency,
            userInfo,
            _,
            true,
            stockItemRequest
          )
        )
      : formatItemValue(
          isCustomApp,
          item.response_key,
          mItem,
          userCurrency,
          userInfo,
          _,
          true,
          stockItemRequest
        );

  const propertyTooltipText =
    sectionName === SearchFilterTypeEnum.PAIRS &&
    isLinkedPair(mItem) &&
    !item?.additional_attributes?.from_master
      ? mItem?.children?.map((child: any) =>
          formatItemValue(
            isCustomApp,
            item.response_key,
            child,
            userCurrency,
            userInfo,
            _,
            false,
            stockItemRequest
          )
        )
      : formatItemValue(
          isCustomApp,
          item.response_key,
          mItem,
          userCurrency,
          userInfo,
          _,
          false,
          stockItemRequest
        );

  return (
    <React.Fragment key={_index}>
      {item.response_key === "available" ? (
        displayImage ? (
          <ImageOverlay
            containerWidth={item?.deafult_width?.split("px")[0]}
            displayImage={displayImage}
          >
            <AvailabilityDot bgColor={theme[getAvailabilityStatus()]} />
            <ImageContainer>
              {imageUri !== "" ? (
                <ImageComponent
                  source={getImageSource(imageUri, ImageSizePreset.LIST_THUMB)}
                  contentFit="cover"
                  {...getIOSImageProps()}
                  onError={() => setImageUri(FALLBACK_IMAGE_ASSET)}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    position: "relative",
                    zIndex: 1,
                  }}
                />
              ) : (
                <View style={{ width: 48, height: 48 }}>
                  <EmptyImagePreview
                    shape={getShapeValue(mItem)}
                    size={40}
                    isPair={isPair(mItem) ?? false}
                  />
                </View>
              )}
            </ImageContainer>
          </ImageOverlay>
        ) : (
          <InfoContainer
            containerWidth={item?.deafult_width?.split("px")[0]}
            displayImage={displayImage}
            fromBulkList={fromBulkList}
            maxWidth={item?.deafult_width?.split("px")[0]}
            width={
              columnWidth?.[storageKey]?.[priceMode]?.[item.name] ||
              item?.deafult_width?.split("px")[0]
            }
            {...(!fromRequestDetail && {
              onLayout: (event) => {
                if (!columnWidths?.[item.name]) {
                  measureColumnWidth(item.name, event);
                }
              },
            })}
          >
            <DotOverlay>
              <TooltipText
                text={propertyText}
                tooltip={propertyText}
                color={theme[getAvailabilityStatus()]}
                type={typography["Minor-R"]}
                enableTooltip={item?.name === "available"}
                isAvailableStatusView={true}
                defaultWidth={
                  columnWidth?.[storageKey]?.[priceMode]?.[item.name] ||
                  defaultWidth
                }
              />
            </DotOverlay>
          </InfoContainer>
        )
      ) : item.response_key === "status" ? (
        <InfoContainer
          containerWidth={defaultWidth}
          displayImage={displayImage}
          alignment={item?.alignment || "flex-start"}
          fromBulkList={fromBulkList}
          maxWidth={defaultWidth}
          width={
            columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
          }
          {...(!fromRequestDetail && {
            onLayout: (event) => {
              if (!columnWidths?.[item.name]) {
                measureColumnWidth(item.name, event);
              }
            },
          })}
          backgroundColor={requestStatus.backgroundColor}
        >
          <VDBText
            type={typography["Minor-R"]}
            color={theme[requestStatus.colorCode]}
          >
            {requestStatus.status}
          </VDBText>
        </InfoContainer>
      ) : item.response_key === "request_item_from_supplier" &&
        priceMode === PriceModes.WHITE &&
        requestItemFromSupplier &&
        enableRequestItemFromSupplier &&
        showRequestItemFromSupplier(stockItemRequest) ? (
        <InfoContainer
          containerWidth={item?.deafult_width?.split("px")[0]}
          displayImage={displayImage}
          alignment={item?.alignment || "flex-start"}
          fromBulkList={fromBulkList}
          maxWidth={item?.deafult_width?.split("px")[0]}
          width={
            columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
          }
          {...(!fromRequestDetail && {
            onLayout: (event) => {
              if (!columnWidth?.[storageKey]?.[priceMode]?.[item?.name]) {
                measureColumnWidth(item?.name, event);
              }
            },
          })}
        >
          <PressableColumn
            onPress={(e) => {
              handleDeepLink(stockItemRequest?.deep_link ?? "");
              e.preventDefault();
            }}
          >
            <VDBText type={typography["Minor-R"]} color={theme["link-static"]}>
              {t(item?.label)}
            </VDBText>
          </PressableColumn>
        </InfoContainer>
      ) : item.response_key === "stone_type_icon" ? (
        <InfoContainer
          containerWidth={
            columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
          }
          displayImage={displayImage}
          fromBulkList={fromBulkList}
          maxWidth={defaultWidth}
          width={
            columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
          }
          alignment={item?.alignment || "flex-start"}
        >
          <VDBIcon
            name={consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.data}
            variant={
              consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.type
            }
            color={theme["core-05"]}
            size={20}
          />
        </InfoContainer>
      ) : (
        <InfoContainer
          containerWidth={item?.deafult_width?.split("px")[0]}
          displayImage={displayImage}
          alignment={item?.alignment || "flex-start"}
          fromBulkList={fromBulkList}
          maxWidth={item?.deafult_width?.split("px")[0]}
          width={
            columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
          }
          {...(!fromRequestDetail && {
            onLayout: (event) => {
              if (!columnWidth?.[storageKey]?.[priceMode]?.[item?.name]) {
                measureColumnWidth(item?.name, event);
              }
            },
          })}
        >
          {Array.isArray(propertyText) && propertyText.length ? (
            <PairChildItemsContainer>
              {propertyText.map((textItem: any, textIndex: any) => {
                if (textIndex > 0 && propertyText[0] === textItem) return null;
                const enableToolTip =
                  item?.enable_tooltip &&
                  mItem?.children?.[textIndex]?.[
                    item?.popover_data_response_key
                  ];
                const showAsPopOver =
                  item?.show_popover_data &&
                  mItem?.children?.[textIndex]?.[
                    item?.popover_data_response_key
                  ];
                const commonProps = {
                  text: textItem,
                  tooltip: item?.tooltip_text
                    ? item?.tooltip_text
                    : propertyTooltipText[textIndex],
                  type: typography["Minor-R"],
                  textAlignment:
                    item?.alignment === "flex-end"
                      ? "right"
                      : item?.alignment === "center"
                      ? "center"
                      : "left",
                  defaultWidth:
                    columnWidth?.[storageKey]?.[priceMode]?.[item.name] - 5 ||
                    defaultWidth,
                  enableTooltip:
                    theme.isLargeScreen &&
                    (item?.name === "Color" ||
                      item?.name === "vendor_name" ||
                      item?.name === "location" ||
                      enableToolTip),
                };
                return showAsPopOver ? (
                  <PressableColumn
                    onPress={(e) => {
                      e.preventDefault();
                      handleLinkClick(
                        mItem?.children?.[textIndex],
                        item?.popover_data_response_key
                      );
                    }}
                  >
                    <TooltipText
                      {...commonProps}
                      color={theme["link-static"]}
                      hoverColor={theme["link-hover"]}
                      defaultWidth={
                        columnWidth?.[storageKey]?.[priceMode]?.[item.name] ||
                        defaultWidth
                      }
                    />
                  </PressableColumn>
                ) : (
                  <TooltipText
                    {...commonProps}
                    color={theme["core-05"]}
                    hoverColor={theme["core-05"]}
                    defaultWidth={
                      columnWidth?.[storageKey]?.[priceMode]?.[item.name] ||
                      defaultWidth
                    }
                  />
                );
              })}
            </PairChildItemsContainer>
          ) : (
            <TooltipText
              text={propertyText}
              tooltip={propertyTooltipText}
              color={theme["core-05"]}
              type={typography["Minor-R"]}
              defaultWidth={
                columnWidth?.[storageKey]?.[priceMode]?.[item.name] ||
                defaultWidth
              }
              enableTooltip={
                theme.isLargeScreen &&
                (item?.name === "Color" ||
                  item?.name === "vendor_name" ||
                  item?.name === "location")
              }
              textAlignment={
                item?.alignment === "flex-end"
                  ? "right"
                  : item?.alignment === "center"
                  ? "center"
                  : "left"
              }
            />
          )}
        </InfoContainer>
      )}
    </React.Fragment>
  );
};

const PressableColumn = styled.Pressable`
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.View`
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-03"]};
`;
const ImageOverlay = styled.View<{
  containerWidth: string;
  displayImage: boolean;
}>`
  flex-direction: row;
  position: relative;
  padding-top: 8px;
  width: ${({ containerWidth }) => containerWidth};
  ${({ displayImage }) =>
    displayImage &&
    css`
      justify-content: center;
      overflow-x: scroll;
    `}
`;

const DotOverlay = styled.View`
  flex-direction: row;
  position: relative;
  justify-content: center;
  align-items: center;
`;

const AvailabilityDot = styled.View<any>`
  height: 8px;
  width: 8px;
  border-radius: 7px;
  border: 1px;
  border-color: ${({ theme }) => theme["cont-00"]};
  background-color: ${({ bgColor }) => bgColor};
  position: absolute;
  top: 51px;
  right: 54px;
  z-index: 2;
`;

const InfoContainer = styled.View<{
  containerWidth?: string;
  displayImage?: boolean;
  alignment?: string;
  fromBulkList?: boolean;
  maxWidth?: string;
  width?: string;
  backgroundColor?: string;
}>`
  padding-top: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-bottom: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-left: ${SpaceType.Basic}px;
  padding-right: ${SpaceType.Small}px;
  margin-right: 3px;
  display: flex;
  width: ${({ width }) => (width ? `${width}px` : "100%")};
  min-width: ${({ containerWidth }) => containerWidth}px;
  align-items: ${({ alignment }) => alignment};
  ${({ displayImage }) => !displayImage && `justify-content: center;`};
  ${({ theme }) => theme.isTablet && `padding-right:8px;padding-left:8px;`};
  ${({ backgroundColor, theme }) =>
    backgroundColor &&
    css`
      background-color: ${theme[backgroundColor]};
      margin-bottom: 1px;
    `}
`;
const PairChildItemsContainer = styled.View`
  flex-direction: column;
  gap: 6px;
`;

export default TabletListItem;
